import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import {
	useAuth,
	useCartContext,
	useThemeContext,
	useWindowListener,
} from '@quizzach/contexts';
import { useFullscreen, useIsSafari, useNotifications } from '@quizzach/hooks';
import { RoutePath } from '@quizzach/interfaces';
import { useGiftBasket } from '../gift/GiftBasket';
import { fadeInConfig } from '@quizzach/motion';
import { scrollToTop } from '@quizzach/utils';
import {
	IcArrowRight,
	IcBasket,
	IcBell,
	IcCards,
	IcChevronDown,
	IcClose,
	Expand,
	ExpandReverse,
	IcGift,
	QLogo,
	IcInfo,
	IcMoon,
	IcPlayOutline,
	IcPlusRound,
	IcSun,
	IcUserRound,
	Xcollab,
} from '@quizzach/assets';
import {
	Colors,
	HandwrittenText,
	LangMenu,
	LangSwitch,
	NavAvatarLink,
	NavBackdrop,
	NavBellButton,
	NavCartLink,
	NavCloseLink,
	NavExpandButton,
	NavCollectionLink,
	NavGhostLink,
	NavSectionLabel,
	ThemeToggle,
	TopNavBar,
} from '@quizzach/styles';
import { SplitLogo } from './SplitLogo';

const LANGUAGE_LABELS = { hr: 'HR', en: 'ENG' } as const;

// The language switch is built but not launched yet - flip to true to show it.
const LANGUAGE_SWITCH_ENABLED: boolean = false;

interface TopNavProps {
	hideLogo?: boolean;
	hideSecondaryItems?: boolean;
	featuredAuthor?: boolean;
	authorDisplayName?: string;
	closeQuizToProfile?: boolean;
	bare?: boolean;
	/* extra content slotted between the logo lockup and the right-side
	   controls — the quiz play bar puts its desktop progress bar here */
	centerContent?: ReactNode;
}

const CollabBadge = ({ authorDisplayName }: { authorDisplayName?: string }) => (
	<motion.div {...fadeInConfig()} className='nav-collab'>
		<Xcollab size={16} color={Colors.brandPale} />
		{authorDisplayName === 'Nogač' ? (
			<img
				src='/collaborators/collaborator_logo-nogac.png'
				alt='Nogac'
				className='nav-logo-nogac'
			/>
		) : authorDisplayName === 'Vragolasti Tenis' ? (
			<img
				src='/collaborators/collaborator_logo-vragolasti.png'
				alt='Vragolasti Tenis'
				className='nav-logo-vragolasti'
			/>
		) : authorDisplayName === 'Putoholičari' ? (
			<img
				src='/collaborators/collaborator_logo-putoholicari.png'
				alt='Putoholičari'
				className='nav-logo-vragolasti'
			/>
		) : authorDisplayName === 'Goodtalking' ? (
			<img
				src='/collaborators/collaborator_logo-goodtalking.png'
				alt='Goodtalking'
				className='nav-logo-vragolasti'
			/>
		) : (
			<HandwrittenText color={Colors.brandPale} $size={24}>
				{authorDisplayName}
			</HandwrittenText>
		)}
	</motion.div>
);

// Wayfinding label shown next to the logo while the user is inside one of the
// creation flows - the bar carries no catalog links, so this is the only
// "where am I" signal it needs.
const useSectionLabelKey = (pathname: string): string | undefined => {
	if (pathname.startsWith(RoutePath.QuickPick)) {
		return 'nav.quickPick';
	}
	if (pathname.startsWith(RoutePath.GeneratorCustomizer)) {
		return 'nav.generator';
	}
	if (pathname.startsWith(RoutePath.PremadeQuizzes)) return 'nav.premade';
	return undefined;
};

export const TopNav = ({
	hideLogo,
	hideSecondaryItems,
	featuredAuthor,
	authorDisplayName,
	closeQuizToProfile,
	bare,
	centerContent,
}: TopNavProps) => {
	const { t, i18n } = useTranslation();
	const { isAuthenticated, profileInfo } = useAuth();
	const { cartCount } = useCartContext();
	const { guestCount, openCart } = useGiftBasket();
	const { theme, toggleTheme } = useThemeContext();
	const { touchDevice } = useWindowListener();
	const { isFullscreen, toggleFullScreen } = useFullscreen();
	const { isSafariTouch } = useIsSafari();
	const location = useLocation();
	const navigate = useNavigate();
	const [scrolled, setScrolled] = useState(false);
	const [hidden, setHidden] = useState(false);
	const [createOpen, setCreateOpen] = useState(false);
	const [notifOpen, setNotifOpen] = useState(false);
	// which content the stage shows; kept across close so the collapse
	// animation doesn't flicker to the other view
	const [stageView, setStageView] = useState<'create' | 'notifs'>('create');
	const [langOpen, setLangOpen] = useState(false);
	const [langMenuPos, setLangMenuPos] = useState({ top: 0, left: 0 });
	const lastYRef = useRef(0);
	const { notifications, dismissNotification } = useNotifications();

	const stageOpen = createOpen || notifOpen;

	const language = i18n.language === 'en' ? 'en' : 'hr';

	const changeLanguage = (next: 'hr' | 'en') => {
		setLangOpen(false);
		if (next === language) return;
		i18n.changeLanguage(next);
		localStorage.setItem('language', next);
	};

	const sectionLabelKey = useSectionLabelKey(location.pathname);
	// note: RoutePath can't be read at module scope here - Routes.interface.ts
	// imports the views (for AppRoutes), which transitively import this file
	const isCartRoute = (
		[
			RoutePath.Summary,
			RoutePath.QuickSummary,
			RoutePath.GeneratorSummary,
		] as string[]
	).includes(location.pathname);

	useEffect(() => {
		let ticking = false;

		const onScroll = () => {
			if (ticking) return;
			ticking = true;
			requestAnimationFrame(() => {
				const y = window.scrollY;
				setScrolled(y > 40);
				// bar leaves as soon as scrolling down starts,
				// returns on any scroll up (all viewports)
				if (y > lastYRef.current + 4 && y > 60) {
					setHidden(true);
				} else if (y < lastYRef.current - 4 || y <= 60) {
					setHidden(false);
				}
				lastYRef.current = y;
				ticking = false;
			});
		};

		window.addEventListener('scroll', onScroll, { passive: true });
		onScroll();
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	useEffect(() => {
		setCreateOpen(false);
		setNotifOpen(false);
		setLangOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		if (!createOpen && !notifOpen && !langOpen) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setCreateOpen(false);
				setNotifOpen(false);
				setLangOpen(false);
			}
		};
		const onDocumentClick = () => {
			setCreateOpen(false);
			setNotifOpen(false);
			setLangOpen(false);
		};

		window.addEventListener('keydown', onKeyDown);
		window.addEventListener('click', onDocumentClick);
		return () => {
			window.removeEventListener('keydown', onKeyDown);
			window.removeEventListener('click', onDocumentClick);
		};
	}, [createOpen, notifOpen, langOpen]);

	// the language menu floats outside the bar (portal), so it would detach
	// from the trigger once the bar starts moving - close it on scroll
	useEffect(() => {
		if (!langOpen) return;

		const onScroll = () => setLangOpen(false);
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, [langOpen]);

	const go = (path: string) => {
		setCreateOpen(false);
		setNotifOpen(false);
		navigate(path);
		scrollToTop();
	};

	const createRows = [
		{
			titleKey: 'nav.quickPick',
			subtitleKey: 'nav.quickPickDescription',
			path: RoutePath.QuickPick,
		},
		{
			titleKey: 'nav.generator',
			subtitleKey: 'nav.generatorDescription',
			path: RoutePath.GeneratorCustomizer,
		},
		{
			titleKey: 'nav.premade',
			subtitleKey: 'nav.premadeDescription',
			path: RoutePath.PremadeQuizzes,
		},
	];

	const stageFootRows = [
		{
			labelKey: 'nav.playFreeQuiz',
			icon: <IcPlayOutline size={15} />,
			path: RoutePath.Profile as string,
		},
		{
			labelKey: 'nav.giftVoucher',
			icon: <IcGift size={15} />,
			path: RoutePath.Voucher as string,
		},
		{
			labelKey: 'nav.howToPlay',
			icon: <IcInfo size={15} />,
			path: RoutePath.Faq as string,
		},
	];

	return (
		<>
			{!bare && !hideSecondaryItems && (
				<NavBackdrop $open={stageOpen} aria-hidden='true' />
			)}

			<TopNavBar
				$scrolled={scrolled}
				$hidden={hidden}
				$bare={bare}
				$open={stageOpen}
			>
				<div className='top'>
					{hideLogo ? (
						<div />
					) : (
						<div style={{ display: 'flex', alignItems: 'center' }}>
							{bare && featuredAuthor && touchDevice ? (
								/* phone collab lockup: Q glyph only, the wordmark
								   would crowd the author name */
								<QLogo size={24} color={Colors.brandPale} />
							) : (
								<SplitLogo />
							)}
							{featuredAuthor && (
								<CollabBadge authorDisplayName={authorDisplayName} />
							)}
							{!bare && !hideSecondaryItems && sectionLabelKey && (
								<NavSectionLabel>{t(sectionLabelKey)}</NavSectionLabel>
							)}
						</div>
					)}

					{centerContent}

					{!hideSecondaryItems && !bare && (
						<div className='center'>
							<NavCollectionLink to={RoutePath.Voucher}>
								<IcGift size={15} />
								{t('nav.giftVoucher')}
							</NavCollectionLink>
							{/* click toggles the stage; Esc / outside click / backdrop close */}
							<button
								type='button'
								className='cta'
								aria-haspopup='menu'
								aria-expanded={createOpen}
								onClick={(e) => {
									e.stopPropagation();
									setNotifOpen(false);
									setStageView('create');
									setCreateOpen((open) => !open);
								}}
							>
								<IcPlusRound size={16} />
								{t('nav.newQuiz')}
							</button>
							{isAuthenticated && (
								<NavCollectionLink to={RoutePath.Profile}>
									<IcCards size={15} />
									{t('nav.myCollection')}
								</NavCollectionLink>
							)}
						</div>
					)}

					<div className='links'>
						{LANGUAGE_SWITCH_ENABLED && !bare && (
							<LangSwitch
								type='button'
								aria-haspopup='menu'
								aria-expanded={langOpen}
								aria-label={t('nav.changeLanguage')}
								onClick={(e) => {
									e.stopPropagation();
									const rect = e.currentTarget.getBoundingClientRect();
									setLangMenuPos({
										top: rect.bottom + 10,
										left: rect.left + rect.width / 2,
									});
									setLangOpen((open) => !open);
								}}
							>
								{LANGUAGE_LABELS[language]}
								<span className='chev'>
									<IcChevronDown size={13} />
								</span>
							</LangSwitch>
						)}

						{/* quiz play is dark-only, so the play bar (bare) has no toggle */}
						{!bare && (
							<ThemeToggle
								type='button'
								aria-label={t('nav.toggleTheme')}
								onClick={toggleTheme}
							>
								<span className={`opt ${theme !== 'dark' ? 'on' : ''}`}>
									<IcSun />
								</span>
								<span className={`opt ${theme === 'dark' ? 'on' : ''}`}>
									<IcMoon />
								</span>
							</ThemeToggle>
						)}

						{/* iOS Safari has no fullscreen API, so the toggle hides there */}
						{bare && closeQuizToProfile && !isSafariTouch && (
							<NavExpandButton
								type='button'
								aria-label={t('nav.fullscreen')}
								onClick={toggleFullScreen}
							>
								{isFullscreen ? (
									<ExpandReverse size={16} />
								) : (
									<Expand size={16} />
								)}
							</NavExpandButton>
						)}

						{!hideSecondaryItems && !bare && isAuthenticated && (
							<NavBellButton
								type='button'
								$open={notifOpen}
								aria-label={t('nav.notifications')}
								aria-expanded={notifOpen}
								onClick={(e) => {
									e.stopPropagation();
									setCreateOpen(false);
									setNotifOpen((open) => {
										if (!open) setStageView('notifs');
										return !open;
									});
								}}
							>
								<IcBell size={19} />
								{notifications.length > 0 && (
									<span className='badge'>
										{notifications.length > 9 ? '9+' : notifications.length}
									</span>
								)}
							</NavBellButton>
						)}

						{!hideSecondaryItems && (
							<>
								{(isAuthenticated || guestCount > 0) && (
									<NavCartLink
										to={RoutePath.Summary}
										aria-label={t('nav.cart')}
										onClick={(e) => {
											if (!isAuthenticated) {
												e.preventDefault();
												openCart();
											}
										}}
										className={({ isActive }) =>
											isActive || isCartRoute ? 'active' : ''
										}
									>
										<IcBasket size={20} />
										{cartCount + guestCount > 0 && (
											<span className='badge'>
												{cartCount + guestCount > 9
													? '9+'
													: cartCount + guestCount}
											</span>
										)}
									</NavCartLink>
								)}

								{isAuthenticated ? (
									<NavAvatarLink
										to={RoutePath.Profile}
										aria-label={t('nav.myQuizzes')}
									>
										{profileInfo ? (
											<img
												src={`/avatars/${profileInfo.profile_picture}.webp`}
												alt=''
											/>
										) : (
											<span className='fb'>
												<IcUserRound size={16} />
											</span>
										)}
									</NavAvatarLink>
								) : (
									<NavGhostLink to={RoutePath.Login}>
										<IcUserRound size={16} />
										{t('auth.login.title')}
									</NavGhostLink>
								)}
							</>
						)}

						{closeQuizToProfile && (
							<NavCloseLink
								to={RoutePath.Profile}
								aria-label={t('nav.closeQuiz')}
							>
								<IcClose size={17} />
							</NavCloseLink>
						)}
					</div>
				</div>

				{!hideSecondaryItems && !bare && (
					<div className='stage' onClick={(e) => e.stopPropagation()}>
						{stageView === 'notifs' ? (
							<div className='nstage'>
								<span className='ncap'>{t('nav.notifications')}</span>
								{notifications.length === 0 ? (
									<p className='nempty'>{t('nav.notificationsEmpty')}</p>
								) : (
									notifications.map((notification) => {
										const { path } = notification;
										const content = (
											<>
												<b>{t(notification.titleKey)}</b>
												{notification.bodyKey && (
													<u>{t(notification.bodyKey)}</u>
												)}
											</>
										);
										return path ? (
											<button
												key={notification.id}
												type='button'
												className='nr'
												onClick={() => {
													dismissNotification(notification.id);
													go(path);
												}}
											>
												{content}
											</button>
										) : (
											<div key={notification.id} className='nr'>
												{content}
											</div>
										);
									})
								)}
							</div>
						) : (
							<>
								<div className='rows'>
									{createRows.map((row, index) => (
										<button
											key={row.titleKey}
											type='button'
											className='irow'
											onClick={() => go(row.path)}
										>
											<span className='num'>{`0${index + 1}`}</span>
											<span>
												<b>{t(row.titleKey)}</b>
												<u>{t(row.subtitleKey)}</u>
											</span>
											<span className='arr'>
												<IcArrowRight size={17} />
											</span>
										</button>
									))}
								</div>

								<div className='sfoot'>
									{stageFootRows.map((row) => (
										<button
											key={row.labelKey}
											type='button'
											className='srow'
											onClick={() => go(row.path)}
										>
											{row.icon}
											{t(row.labelKey)}
										</button>
									))}
								</div>
							</>
						)}
					</div>
				)}
			</TopNavBar>

			{LANGUAGE_SWITCH_ENABLED &&
				langOpen &&
				createPortal(
					<LangMenu
						role='menu'
						style={{ top: langMenuPos.top, left: langMenuPos.left }}
						onClick={(e) => e.stopPropagation()}
					>
						{(['hr', 'en'] as const).map((lang) => (
							<button
								key={lang}
								type='button'
								role='menuitem'
								className={lang === language ? 'on' : ''}
								onClick={() => changeLanguage(lang)}
							>
								{LANGUAGE_LABELS[lang]}
							</button>
						))}
					</LangMenu>,
					document.body
				)}
		</>
	);
};
